import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Flame } from "lucide-react";
import api, { formatError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Le mot de passe doit contenir au moins 6 caractères.");
    if (password !== confirm) return toast.error("Les mots de passe ne correspondent pas.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Mot de passe réinitialisé 💪");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 bg-background">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8 cursor-pointer" onClick={() => nav("/")}>
          <Flame className="text-primary" size={28} strokeWidth={2.5} />
          <span className="font-display text-3xl">ROX<span className="text-primary">LAB</span></span>
        </div>
        <div className="bg-card border border-border rounded-lg p-6 md:p-8" data-testid="reset-password-card">
          {!token ? (
            <div className="text-center">
              <h1 className="font-display text-2xl mb-2">Lien invalide</h1>
              <p className="text-sm text-muted-foreground mb-5">Ce lien de réinitialisation est incomplet ou expiré.</p>
              <Button data-testid="reset-back-login-btn" onClick={() => nav("/auth")} className="bg-primary hover:bg-primary/90 text-white rounded-md h-12 px-6 font-semibold">Retour à la connexion</Button>
            </div>
          ) : done ? (
            <div className="text-center">
              <h1 className="font-display text-2xl mb-2">C'est fait ✅</h1>
              <p className="text-sm text-muted-foreground mb-5">Ton mot de passe a été réinitialisé. Tu peux maintenant te connecter.</p>
              <Button data-testid="reset-goto-login-btn" onClick={() => nav("/auth")} className="w-full bg-primary hover:bg-primary/90 text-white rounded-md h-12 font-semibold">Se connecter</Button>
            </div>
          ) : (
            <>
              <h1 className="font-display text-3xl mb-1">Nouveau mot de passe</h1>
              <p className="text-sm text-muted-foreground mb-6">Choisis un mot de passe d'au moins 6 caractères.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Mot de passe</Label>
                  <Input data-testid="reset-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-12 text-base mt-1 bg-secondary border-border" placeholder="••••••" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Confirmer</Label>
                  <Input data-testid="reset-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="h-12 text-base mt-1 bg-secondary border-border" placeholder="••••••" />
                </div>
                <Button data-testid="reset-submit-btn" type="submit" disabled={loading} className="w-full h-13 py-3 bg-primary hover:bg-primary/90 text-white rounded-md text-base font-semibold active:scale-95 transition-transform">
                  {loading ? "…" : "Réinitialiser mon mot de passe"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
